import { useGetIdentity, useTranslate } from 'react-admin';
import { Button, Stack, Typography } from '@mui/material';

import type { Vault } from 'src/pages/admin/services/vaultService';
import { PermissionButton } from 'src/routes/components/permission-button';

import { VAULTS_NS as NS, VAULTS_PERMISSION_RESOURCE as RESOURCE } from './constants';
import { type ClosureAction, closureActions, formatInstant } from './helpers';
import type { VaultClosureForm } from './useVaultClosure';

const COLOR = { request: 'error', approve: 'error', cancel: 'primary' } as const;

/**
 * XBIT-4243 (Module PRD §14.5 关闭金库): the detail's Close vault section. It shows the vault's live closure — state,
 * who asked and approved, when the notice ends — and offers request, approve or cancel (`closureActions`). While closing
 * is shipped dark the button is disabled with that reason; a vault that is not flat is refused by the engine, and the
 * confirm shows that refusal as it comes back.
 */
export const ClosureBlock = ({ vault, form }: { vault: Vault; form: VaultClosureForm }) => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  const t = (key: string, options?: object) => translate(`${NS}.detail.closure.${key}`, options);
  const { closure } = vault;
  const operatorId = identity?.id === undefined ? undefined : String(identity.id);
  const actions = closureActions(vault, form.available, operatorId);

  return (
    <Stack spacing={1.5} sx={{ gridColumn: '1 / -1' }}>
      {closure ? (
        <>
          <Typography variant="body2">
            {t(`state.${closure.state}`)}
            {closure.freezeAt && (
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                {' · '}
                {t('freezeAt', { at: formatInstant(closure.freezeAt) })}
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('approvers', {
              approvers: (closure.approverNames ?? closure.approvers).join(', '),
              count: closure.approvers.length,
            })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>
            {t('reasonLine', { reason: closure.reason })}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('none')}
        </Typography>
      )}
      {actions.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {actions.map(({ action, disabled }) => (
            <ActionButton
              key={action}
              action={action}
              disabledReason={disabled}
              busy={form.busy}
              onClick={() => form.open(action)}
              t={t}
            />
          ))}
        </Stack>
      )}
      {actions.some((a) => a.disabled) && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t(`disabled.${actions.find((a) => a.disabled)?.disabled}`)}
        </Typography>
      )}
    </Stack>
  );
};

const ActionButton = ({
  action,
  disabledReason,
  busy,
  onClick,
  t,
}: {
  action: ClosureAction;
  disabledReason: string | null;
  busy: boolean;
  onClick: () => void;
  t: (key: string, options?: object) => string;
}) => (
  <PermissionButton resource={RESOURCE} action="update">
    <Button
      variant="outlined"
      color={COLOR[action]}
      disabled={busy || Boolean(disabledReason)}
      onClick={onClick}
    >
      {t(`action.${action}`)}
    </Button>
  </PermissionButton>
);
